/**
 * LevelUpFX — High-voltage kinetic particle & electric arc FX controller
 * for the Level-Up Banner.
 *
 * Provides:
 * 1. "Reinhauen": Supersonic kinetic impact shockwave, photon blast & anamorphic flare.
 * 2. "Funkenregen": Explosive shower of 72 golden, crystal-blue and searing-white sparks.
 * 3. "Lichtbogen": Procedural high-voltage electric plasma arcs crackling across
 *    the central medallion and branching to both laser lines.
 * 4. "Core Blast": Initial contact energy burst at t <= 0.2s.
 *
 * Designed to be easily "gebacken" (pre-rendered / baked into spritesheet or frames):
 * - Physics are closed-form kinematic equations: currentX(t), currentY(t).
 * - Arc paths and particle bursts are deterministic when a seed is provided (Mulberry32 PRNG).
 * - Exposes `bakeFrames()` and `bakeSpritesheet()` for offline or build-time asset generation.
 * - Runs live at 60fps in browser via requestAnimationFrame with automatic cleanup.
 */

// Simple deterministic 32-bit PRNG (Mulberry32)
function createPrng(seed = 42) {
  let a = (seed ^ 0x12345678) >>> 0;
  return function() {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Environment-safe animation frame helpers
function safeRequestAnimationFrame(cb) {
  if (typeof requestAnimationFrame === 'function') {
    return requestAnimationFrame(cb);
  }
  return setTimeout(() => cb(performance.now()), 16);
}

function safeCancelAnimationFrame(id) {
  if (typeof cancelAnimationFrame === 'function') {
    return cancelAnimationFrame(id);
  }
  clearTimeout(id);
}

export class LevelUpFX {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas ? canvas.getContext?.('2d') : null;
    this._animId = null;
    this._startTime = 0;
    this._sparks = [];
    this._activeArcPaths = [];
    this._arcVariations = [];
    this._lastJitterFrame = -1;
    this._isRunning = false;
    this._anchorX = null;
    this._anchorY = null;
    this._rng = Math.random;

    this.width = canvas?.width || 640;
    this.height = canvas?.height || 420;
  }

  /**
   * Initializes or re-seeds the FX state for an impact slam.
   * @param {HTMLElement|null} anchorEl - Optional DOM element (e.g. icon capsule) to center FX on
   * @param {Object} options - { seed: number|null, sparkCount: number }
   */
  start(anchorEl = null, options = {}) {
    if (!this.canvas || !this.ctx) return;

    this._isRunning = true;
    this._startTime = performance.now();
    this.width = this.canvas.width;
    this.height = this.canvas.height;
    this._lastJitterFrame = -1;

    // Use deterministic PRNG if seed is passed, else Math.random
    this._rng = typeof options.seed === 'number' ? createPrng(options.seed) : Math.random;

    // Calculate anchor point (default center of canvas or relative to anchorEl)
    let centerX = this.width / 2;
    let centerY = 118; // Default medallion vertical center in canvas

    if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function' && typeof this.canvas.getBoundingClientRect === 'function') {
      try {
        const cRect = this.canvas.getBoundingClientRect();
        const aRect = anchorEl.getBoundingClientRect();
        if (cRect.width > 0 && cRect.height > 0) {
          centerX = (aRect.left + aRect.width / 2) - cRect.left;
          centerY = (aRect.top + aRect.height / 2) - cRect.top;
        }
      } catch (err) {
        // Fallback to defaults
      }
    }

    this._anchorX = centerX;
    this._anchorY = centerY;

    // 1. Generate Funkenregen (72 high-velocity luminous spark particles)
    this._sparks = [];
    const sparkCount = typeof options.sparkCount === 'number' ? options.sparkCount : 72;
    const sparkColors = ['#ffffff', '#fbbf24', '#facc15', '#38bdf8', '#7dd3fc', '#f59e0b', '#e0f2fe', '#00e5ff'];
    const rng = this._rng;

    for (let i = 0; i < sparkCount; i++) {
      // Explosive conical burst downward and outward with high velocity
      const angle = (rng() * Math.PI * 1.5) + (Math.PI * 0.75); // downward 270-deg cone
      const speed = 180 + (rng() * 420); // 180 - 600 px/sec
      const size = 1.4 + (rng() * 2.8);
      const life = 0.8 + (rng() * 1.05); // 0.8s to 1.85s duration

      this._sparks.push({
        x: centerX + (rng() - 0.5) * 44,
        y: centerY + (rng() - 0.5) * 44,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 80, // Heavy initial supersonic lift
        gravity: 460 + (rng() * 220), // Heavy gravity acceleration
        color: sparkColors[i % sparkColors.length],
        size,
        life,
        maxLife: life
      });
    }

    // 2. Pre-generate deterministic arc variations for crackling
    this._precalcArcVariations(centerX, centerY);

    // 3. Animation loop
    this._loop = (now) => {
      if (!this._isRunning) return;
      const elapsedSec = (now - this._startTime) / 1000;
      this.renderAtTime(elapsedSec);

      if (elapsedSec < 2.0) {
        this._animId = safeRequestAnimationFrame(this._loop);
      } else {
        this.stop();
      }
    };

    if (this._animId) safeCancelAnimationFrame(this._animId);
    this._animId = safeRequestAnimationFrame(this._loop);
  }

  _precalcArcVariations(cx, cy) {
    // Generate 16 distinct jitter variations of the electric arcs
    this._arcVariations = [];
    const rng = this._rng;

    for (let v = 0; v < 16; v++) {
      const arcs = [];

      // Arc 1: Leaping violently across top rim of medallion
      const arc1Start = { x: cx - 38 + (rng() * 8), y: cy - 24 + (rng() * 8) };
      const arc1End = { x: cx + 36 + (rng() * 8), y: cy - 30 + (rng() * 8) };
      arcs.push({
        color: '#38bdf8',
        width: 1.8,
        points: this._displaceMidpoint(arc1Start.x, arc1Start.y, arc1End.x, arc1End.y, 16, 5, rng)
      });

      // Arc 2: Leaping downward along gold border
      const arc2Start = { x: cx + 36, y: cy - 12 + (rng() * 6) };
      const arc2End = { x: cx + 22 + (rng() * 12), y: cy + 36 };
      arcs.push({
        color: '#fbbf24',
        width: 1.4,
        points: this._displaceMidpoint(arc2Start.x, arc2Start.y, arc2End.x, arc2End.y, 13, 4, rng)
      });

      // Arc 3: Leaping leftward to left laser header line
      if (rng() > 0.2) {
        const arc3Start = { x: cx - 28, y: cy - 32 };
        const arc3End = { x: cx - 88 + (rng() * 25), y: cy - 54 };
        arcs.push({
          color: '#00e5ff',
          width: 1.2,
          points: this._displaceMidpoint(arc3Start.x, arc3Start.y, arc3End.x, arc3End.y, 14, 4, rng)
        });
      }

      // Arc 4: Leaping rightward to right laser header line
      if (rng() > 0.2) {
        const arc4Start = { x: cx + 28, y: cy - 32 };
        const arc4End = { x: cx + 88 - (rng() * 25), y: cy - 54 };
        arcs.push({
          color: '#38bdf8',
          width: 1.2,
          points: this._displaceMidpoint(arc4Start.x, arc4Start.y, arc4End.x, arc4End.y, 14, 4, rng)
        });
      }

      this._arcVariations.push(arcs);
    }
  }

  /**
   * Deterministic render frame at exact time `t` in seconds.
   * Perfect for both live requestAnimationFrame and offline baking!
   */
  renderAtTime(t) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    if (typeof ctx.clearRect === 'function') {
      ctx.clearRect(0, 0, this.width, this.height);
    }

    const centerX = this._anchorX ?? (this.width / 2);
    const centerY = this._anchorY ?? 118;

    // --- 0. INITIAL CORE CONTACT BLAST (t <= 0.22s) ---
    if (t >= 0.02 && t <= 0.24) {
      const blastProgress = (t - 0.02) / 0.22;
      const blastAlpha = Math.max(0, 1 - blastProgress);
      const blastRadius = 24 + blastProgress * 76;

      ctx.save?.();
      if (ctx.globalCompositeOperation !== undefined) ctx.globalCompositeOperation = 'lighter';
      if (typeof ctx.createRadialGradient === 'function') {
        try {
          const grad = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, blastRadius);
          grad.addColorStop(0, `rgba(255, 255, 255, ${0.9 * blastAlpha})`);
          grad.addColorStop(0.4, `rgba(56, 189, 248, ${0.6 * blastAlpha})`);
          grad.addColorStop(0.8, `rgba(251, 191, 36, ${0.3 * blastAlpha})`);
          grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
          ctx.fillStyle = grad;
          ctx.beginPath?.();
          ctx.arc?.(centerX, centerY, blastRadius, 0, Math.PI * 2);
          ctx.fill?.();
        } catch (e) {
          // Fallback if gradient not supported in mock
        }
      }
      ctx.restore?.();
    }

    // --- A. LICHTBOGEN (Electric Arcs: active between 0.04s and 1.45s) ---
    if (t >= 0.04 && t <= 1.45 && this._arcVariations?.length > 0) {
      const varIndex = Math.floor(t * 26) % this._arcVariations.length;
      const activeArcs = this._arcVariations[varIndex];

      ctx.save?.();
      if (ctx.globalCompositeOperation !== undefined) ctx.globalCompositeOperation = 'lighter';
      if (ctx.lineCap !== undefined) ctx.lineCap = 'round';
      if (ctx.lineJoin !== undefined) ctx.lineJoin = 'round';

      const arcFade = t > 0.95 ? Math.max(0, (1.45 - t) / 0.5) : 1.0;

      for (const arc of activeArcs) {
        // Outer cyan/gold bloom
        ctx.strokeStyle = arc.color;
        ctx.lineWidth = arc.width * 3.8;
        ctx.globalAlpha = 0.6 * arcFade;
        this._strokePolyline(ctx, arc.points);

        // Inner searing white lightning core
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = Math.max(1, arc.width);
        ctx.globalAlpha = 0.98 * arcFade;
        this._strokePolyline(ctx, arc.points);

        // Terminating sparklet
        const end = arc.points[arc.points.length - 1];
        if (end && ctx.beginPath) {
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(end.x, end.y, 2.5, 0, Math.PI * 2);
          ctx.fill?.();
        }
      }
      ctx.restore?.();
    }

    // --- B. FUNKENREGEN (Spark Physics & Trajectory Trails) ---
    ctx.save?.();
    if (ctx.globalCompositeOperation !== undefined) ctx.globalCompositeOperation = 'lighter';

    for (const s of this._sparks) {
      if (t > s.maxLife) continue;

      // Closed-form kinematic equations: currentX(t), currentY(t)
      const currentX = s.x + s.vx * t;
      const currentY = s.y + s.vy * t + 0.5 * s.gravity * t * t;
      // Tail position from 0.04s in the past
      const tTail = Math.max(0, t - 0.04);
      const tailX = s.x + s.vx * tTail;
      const tailY = s.y + s.vy * tTail + 0.5 * s.gravity * tTail * tTail;

      const progress = t / s.maxLife;
      const alpha = Math.max(0, 1 - Math.pow(progress, 1.6));

      ctx.strokeStyle = s.color;
      ctx.lineWidth = Math.max(1.0, s.size * (1 - progress * 0.55));
      ctx.globalAlpha = alpha;

      if (ctx.beginPath) {
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(currentX, currentY);
        ctx.stroke?.();
      }

      // Glowing spark head
      if (ctx.beginPath) {
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = Math.min(1, alpha * 1.35);
        ctx.beginPath();
        ctx.arc(currentX, currentY, Math.max(0.7, s.size * 0.65), 0, Math.PI * 2);
        ctx.fill?.();
      }
    }

    ctx.restore?.();
  }

  _displaceMidpoint(x1, y1, x2, y2, maxDisplacement, iterations, rng) {
    let points = [{ x: x1, y: y1 }, { x: x2, y: y2 }];

    for (let it = 0; it < iterations; it++) {
      const nextPoints = [];
      const disp = maxDisplacement / Math.pow(1.7, it);

      for (let i = 0; i < points.length - 1; i++) {
        const p1 = points[i];
        const p2 = points[i + 1];
        nextPoints.push(p1);

        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len;
        const ny = dx / len;
        const offset = (rng() - 0.5) * disp;

        nextPoints.push({
          x: midX + nx * offset,
          y: midY + ny * offset
        });
      }
      nextPoints.push(points[points.length - 1]);
      points = nextPoints;
    }

    return points;
  }

  _strokePolyline(ctx, points) {
    if (!points || points.length < 2 || !ctx.beginPath) return;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke?.();
  }

  /**
   * Stops active loop and clears canvas.
   */
  stop() {
    this._isRunning = false;
    if (this._animId) {
      safeCancelAnimationFrame(this._animId);
      this._animId = null;
    }
    if (this.ctx && typeof this.ctx.clearRect === 'function') {
      this.ctx.clearRect(0, 0, this.width, this.height);
    }
  }

  // =========================================================================
  // BAKING / OFFLINE EXPORT UTILITIES ("Gut Gebacken Werden Kann")
  // =========================================================================

  /**
   * Bakes all frames across `duration` seconds at given `fps` into an array of offscreen canvases.
   * Deterministic if `seed` is provided.
   */
  bakeFrames({ fps = 30, duration = 1.5, seed = 42, width = this.width, height = this.height } = {}) {
    const totalFrames = Math.round(duration * fps);
    const frames = [];

    // Temporarily re-initialize with the specified seed
    this.start(null, { seed });
    if (this._animId) {
      safeCancelAnimationFrame(this._animId);
      this._animId = null;
    }

    for (let i = 0; i < totalFrames; i++) {
      const tSec = i / fps;
      const offscreen = typeof document !== 'undefined' && document.createElement
        ? document.createElement('canvas')
        : { width, height, getContext: () => this.ctx };
      offscreen.width = width;
      offscreen.height = height;

      const savedCanvas = this.canvas;
      const savedCtx = this.ctx;
      this.canvas = offscreen;
      this.ctx = offscreen.getContext?.('2d');

      this.renderAtTime(tSec);

      frames.push(offscreen);

      this.canvas = savedCanvas;
      this.ctx = savedCtx;
    }

    this.stop();
    return frames;
  }

  /**
   * Bakes all animation frames into a single compact spritesheet canvas.
   * Returns metadata and the stitched spritesheet canvas.
   */
  bakeSpritesheet({ fps = 30, duration = 1.5, columns = 6, seed = 42, frameWidth = this.width, frameHeight = this.height } = {}) {
    const frames = this.bakeFrames({ fps, duration, seed, width: frameWidth, height: frameHeight });
    const totalFrames = frames.length;
    const rows = Math.ceil(totalFrames / columns);

    const sheetCanvas = typeof document !== 'undefined' && document.createElement
      ? document.createElement('canvas')
      : { width: columns * frameWidth, height: rows * frameHeight, getContext: () => this.ctx };

    sheetCanvas.width = columns * frameWidth;
    sheetCanvas.height = rows * frameHeight;

    const sCtx = sheetCanvas.getContext?.('2d');

    frames.forEach((frameCanvas, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const destX = col * frameWidth;
      const destY = row * frameHeight;
      sCtx?.drawImage?.(frameCanvas, destX, destY);
    });

    return {
      canvas: sheetCanvas,
      totalFrames,
      fps,
      duration,
      columns,
      rows,
      frameWidth,
      frameHeight
    };
  }
}
